import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Shell } from "@/components/Shell";
import { CASALE } from "@/data/questionnaire";
import { getEvent, upsertEvent, computeMargin, economicsBlocks } from "@/data/events";
import { EmailButton } from "@/components/EmailButton";
import { HandoffButton } from "@/components/HandoffButton";

const eur = (v) => (Number(v) || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const inp = "w-full bg-transparent outline-none border-b border-dashed border-[#CDBDA7] focus:border-[#C85A32] py-1 print:border-0";

const DEFAULT_QUOTE = {
  number: "",
  validityDays: 15,
  depositPct: 30,
  includes: "Aperitivo di benvenuto con finger food, menù completo come da proposta allegata, acqua, vino della casa e caffè, torta nuziale, servizio ai tavoli, mise en place, uso della sala interna e del prato, parcheggio.",
  extrasText: "",
  conditions: "Il numero definitivo dei coperti va confermato 7 giorni prima dell'evento e costituisce il minimo fatturabile. Acconto alla firma, saldo entro 3 giorni dall'evento. In caso di pioggia l'evento si svolge nella sala interna. Allergie e intolleranze vanno comunicate per iscritto entro 7 giorni dall'evento. Eventuali servizi aggiuntivi richiesti il giorno stesso saranno conteggiati a parte.",
  closing: "Restiamo a disposizione per visitare insieme il Casale e definire il menù. Sarà un piacere accogliervi.",
};

export default function QuotePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(() => getEvent(id));
  if (!ev) {
    navigate("/eventi", { replace: true });
    return null;
  }
  const q = { ...DEFAULT_QUOTE, ...(ev.quote || {}) };
  const setQ = (patch) => setEv(upsertEvent({ ...ev, quote: { ...q, ...patch } }));
  const m = computeMargin(ev);
  const guests = Number(ev.guests) || 0;
  const price = Number(ev.costs?.pricePerCover) || 0;
  const extrasPrice = Number(q.extrasPrice) || 0;
  const total = guests * price + extrasPrice;
  const deposit = Math.round((total * (Number(q.depositPct) || 0)) / 100);
  const today = new Date().toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  const quoteDoc = () => ({
    kind: "quote", title: `${CASALE.name} · preventivo ${q.number || ""}`, subtitle: `${ev.client || "Cliente"} · ${ev.title || ev.type} · ${ev.date || "data da definire"} · ${guests} ospiti`,
    blocks: [
      { heading: "La proposta comprende", paragraphs: [q.includes] },
      { heading: "Importi", rows: [[`Menù e servizio · ${guests} × ${eur(price)}`, eur(guests * price)], ...(extrasPrice ? [[q.extrasText || "Servizi aggiuntivi", eur(extrasPrice)]] : []), ["Totale", eur(total)], [`Acconto ${q.depositPct}%`, eur(deposit)], ["Saldo", eur(total - deposit)]] },
      { heading: "Condizioni", paragraphs: [q.conditions, q.closing] },
      { heading: "Solo per te (non per il cliente)", paragraphs: [`Margine previsto ${eur(m.margin)} (${m.pct}%)`] },
    ],
  });

  return (
    <Shell wide header={
      <div className="flex gap-3 no-print">
        <button className="btn btn-ghost !min-h-[44px] text-sm" onClick={() => navigate(`/eventi/${id}`)} data-testid="quote-back-btn"><ArrowLeft size={16} /> Scheda evento</button>
        <EmailButton className="btn btn-ghost !min-h-[44px] text-sm" build={quoteDoc} />
        <HandoffButton className="btn btn-ghost !min-h-[44px] text-sm" build={() => ({ ...quoteDoc(), blocks: [...quoteDoc().blocks, ...economicsBlocks(ev).slice(0, 1)] })} />
        <button className="btn btn-olive !min-h-[44px] text-sm" onClick={() => window.print()} data-testid="quote-print-btn"><Printer size={16} /> Stampa / PDF</button>
      </div>
    }>
      {m.revenue > 0 && (
        <div className="no-print mb-6 rounded-2xl border p-4 text-sm flex flex-wrap gap-x-6 gap-y-1" style={{ borderColor: m.pct >= 35 ? "#2E6B4055" : m.pct >= 20 ? "#C27D0055" : "#A8323255", background: m.pct >= 35 ? "#2E6B4010" : m.pct >= 20 ? "#C27D0010" : "#A8323210" }} data-testid="quote-margin-hint">
          <span className="font-semibold text-[#2C221E]">Solo per te:</span>
          <span>margine previsto {eur(m.margin)} ({m.pct}%)</span>
          <span>costo pieno a coperto {eur(guests ? m.total / guests : 0)}</span>
          <span>prezzo minimo per il 30% di margine: {eur(guests ? (m.total / guests) / 0.7 : 0)} a coperto</span>
        </div>
      )}

      <article className="plan fade-up card p-7 sm:p-12 grid gap-8" data-testid="quote-document">
        <div className="flex flex-wrap justify-between gap-6 border-b border-[#E3D5C3] pb-6">
          <div>
            <p className="eyebrow">Preventivo</p>
            <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight text-[#2C221E] mt-1">{CASALE.name}</h1>
            <p className="text-sm text-[#7A6B63] mt-1">{CASALE.tagline}</p>
          </div>
          <div className="text-sm text-[#5C4D46] text-right grid gap-1">
            <p>Data: {today}</p>
            <p className="flex items-center justify-end gap-1">N. <input className={`${inp} w-20 text-right`} value={q.number} onChange={(e) => setQ({ number: e.target.value })} placeholder="2026-01" data-testid="quote-number-input" /></p>
            <p className="flex items-center justify-end gap-1">Valido <input type="number" className={`${inp} w-12 text-right`} value={q.validityDays} onChange={(e) => setQ({ validityDays: e.target.value })} /> giorni</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 text-[#2C221E]">
          <div>
            <p className="eyebrow">Spettabile</p>
            <p className="font-serif text-2xl font-semibold mt-1" data-testid="quote-client">{ev.client || "Gentile cliente"}</p>
          </div>
          <div>
            <p className="eyebrow">Evento</p>
            <p className="font-serif text-2xl font-semibold mt-1">{ev.title || ev.type}</p>
            <p className="text-sm text-[#5C4D46]">{ev.date ? new Date(ev.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "Data da definire"} · {guests} ospiti · {ev.spaces.join(", ")}</p>
          </div>
        </div>

        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-2">La proposta comprende</h2>
          <textarea className={`${inp} min-h-[90px] resize-none leading-relaxed`} style={{ fieldSizing: "content" }} value={q.includes} onChange={(e) => setQ({ includes: e.target.value })} data-testid="quote-includes-input" />
        </section>

        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-3">Importi</h2>
          <table className="w-full text-[#2C221E]">
            <tbody>
              <tr className="border-b border-[#E3D5C3]"><td className="py-3">Menù e servizio · {guests} ospiti × {eur(price)} a persona</td><td className="py-3 text-right font-semibold" data-testid="quote-menu-total">{eur(guests * price)}</td></tr>
              <tr className="border-b border-[#E3D5C3]">
                <td className="py-3"><input className={inp} value={q.extrasText} onChange={(e) => setQ({ extrasText: e.target.value })} placeholder="Servizi aggiuntivi (allestimento floreale, musica, camere…)" data-testid="quote-extras-text" /></td>
                <td className="py-3 text-right w-40"><input type="number" className={`${inp} text-right`} value={q.extrasPrice ?? ""} onChange={(e) => setQ({ extrasPrice: e.target.value })} placeholder="0" data-testid="quote-extras-price" /></td>
              </tr>
              <tr><td className="py-4 font-serif text-2xl font-semibold text-[#2D4030]">Totale</td><td className="py-4 text-right font-serif text-3xl font-bold text-[#2D4030]" data-testid="quote-total">{eur(total)}</td></tr>
            </tbody>
          </table>
          <p className="text-sm text-[#5C4D46] flex flex-wrap items-center gap-1">Acconto alla conferma: <input type="number" className={`${inp} w-12 text-right`} value={q.depositPct} onChange={(e) => setQ({ depositPct: e.target.value })} data-testid="quote-deposit-pct" />% = <strong className="text-[#2C221E]" data-testid="quote-deposit">{eur(deposit)}</strong> · saldo {eur(total - deposit)}. Importi IVA inclusa.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl font-semibold text-[#2D4030] mb-2">Condizioni</h2>
          <textarea className={`${inp} min-h-[90px] resize-none text-sm leading-relaxed text-[#5C4D46]`} style={{ fieldSizing: "content" }} value={q.conditions} onChange={(e) => setQ({ conditions: e.target.value })} data-testid="quote-conditions-input" />
        </section>

        <textarea className={`${inp} resize-none italic text-[#5C4D46]`} style={{ fieldSizing: "content" }} rows={2} value={q.closing} onChange={(e) => setQ({ closing: e.target.value })} />

        <div className="grid grid-cols-2 gap-10 mt-4 text-sm text-[#7A6B63]">
          <div><div className="border-b border-[#8A7970] h-10" />{CASALE.name}</div>
          <div><div className="border-b border-[#8A7970] h-10" />Per accettazione</div>
        </div>
      </article>
    </Shell>
  );
}
